import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import StatusChip from "../../components/purchase/StatusChip";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { checkPrice, isPriceBlocked } from "../../utils/PriceCalculator";
import {
  CHIP_CLASS,
  getPlanTypeLabel,
  PACKAGE_CONFIG_LABEL_KEY,
  VISIBILITY_TYPE_CHIP_CLASS,
  VISIBILITY_TYPE_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import {
  PACKAGE_CONFIG_KEY_ENUM,
  PACKAGE_EDITOR_TAB_ENUM,
} from "../../utils/PurchaseEnumerations";
import PackageClaimsTab from "./PackageClaimsTab";
import PackageDetailsTab from "./PackageDetailsTab";
import {
  fetchPackageDetail,
  getPackageContentError,
  getPackageEditorInitialValues,
  handleSubmitPackage,
  IPackageEditorForm,
  isFreeTrialPackage,
  ISubscriptionPackageDetail,
  packageEditorValidationSchema,
} from "./PackageEditorController";
import PackageFeaturesTab from "./PackageFeaturesTab";
import PackageLimitsTab from "./PackageLimitsTab";
import PackagePriceTab from "./PackagePriceTab";

interface IPackageEditorProps {
  packageId: string;
  readOnly: boolean;
  handleClose: (status: boolean) => void;
}

const TAB_LABEL_KEY: Record<PACKAGE_EDITOR_TAB_ENUM, string> = {
  [PACKAGE_EDITOR_TAB_ENUM.Details]: "details",
  [PACKAGE_EDITOR_TAB_ENUM.Price]: "tab_price_tax",
  [PACKAGE_EDITOR_TAB_ENUM.Limits]: "tab_limits_credits",
  [PACKAGE_EDITOR_TAB_ENUM.Features]: "tab_features",
  [PACKAGE_EDITOR_TAB_ENUM.Claims]: "claims",
};

const isConfigKey = (key: string): key is PACKAGE_CONFIG_KEY_ENUM =>
  Object.values(PACKAGE_CONFIG_KEY_ENUM).some((configKey) => configKey === key);

const PackageEditor: React.FC<IPackageEditorProps> = ({ packageId, readOnly, handleClose }) => {
  const { t } = UseTranslationHook();
  const [detail, setDetail] = useState<ISubscriptionPackageDetail>();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(PACKAGE_EDITOR_TAB_ENUM.Details);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const close = UseEventCallbackHook(handleClose);

  useEffect(() => {
    fetchPackageDetail(packageId, setDetail).then((loaded) => {
      setLoading(false);
      if (!loaded) close(false);
    });
  }, [packageId, close]);

  if (loading || !detail) return <Loader />;

  const isFreeTrial = isFreeTrialPackage(detail);
  const initialValues = getPackageEditorInitialValues(detail);
  const labelOf = (key: string) => (isConfigKey(key) ? t(PACKAGE_CONFIG_LABEL_KEY[key]) : key);

  const title = (
    <span className="inline-flex items-center gap-2 normal-case">
      {detail.name}
      <StatusChip label={getPlanTypeLabel(t, detail.platforms)} className={CHIP_CLASS.info} />
      <StatusChip
        label={t(VISIBILITY_TYPE_LABEL_KEY[detail.visibility_type])}
        className={VISIBILITY_TYPE_CHIP_CLASS[detail.visibility_type]}
      />
    </span>
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={packageEditorValidationSchema(t)}
        onSubmit={async (values: IPackageEditorForm) => {
          if (await handleSubmitPackage(packageId, values, setSubmitLoading)) close(true);
        }}
      >
        {(formikProps) => {
          const { values, dirty } = formikProps;
          const contentError = getPackageContentError(t, values, labelOf);
          const priceBlocked =
            !isFreeTrial &&
            isPriceBlocked(
              checkPrice(values.base_price, { cgst: values.cgst, sgst: values.sgst, igst: values.igst }),
              false,
            );
          const isBlocked = !!contentError || priceBlocked;

          return (
            <FormRightModalWrapper
              containerClassName="!w-2/3"
              onClose={() => (dirty && !readOnly ? setShowDiscardWarning(true) : close(false))}
              title={readOnly ? t("view_package") : t("edit_package")}
            >
              <Form onSubmit={formikProps.handleSubmit} className="flex flex-col h-full">
                <div className="mb-3">{title}</div>
                <div className="flex gap-1 border-b mb-4 overflow-x-scroll">
                  {Object.values(PACKAGE_EDITOR_TAB_ENUM).map((tab) => (
                    <ICButton
                      type="button"
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={cn("w-fit px-3 py-2 rounded-none rounded-t-md whitespace-nowrap", {
                        "bg-accent-muted text-accent font-semibold": activeTab === tab,
                        "bg-transparent text-muted": activeTab !== tab,
                      })}
                    >
                      {t(TAB_LABEL_KEY[tab])}
                    </ICButton>
                  ))}
                </div>
                <div className="flex-1 overflow-y-scroll pr-1">
                  {activeTab === PACKAGE_EDITOR_TAB_ENUM.Details && (
                    <PackageDetailsTab formikProps={formikProps} isFreeTrial={isFreeTrial} readOnly={readOnly} />
                  )}
                  {activeTab === PACKAGE_EDITOR_TAB_ENUM.Price && (
                    <PackagePriceTab formikProps={formikProps} isFreeTrial={isFreeTrial} readOnly={readOnly} />
                  )}
                  {activeTab === PACKAGE_EDITOR_TAB_ENUM.Limits && (
                    <PackageLimitsTab formikProps={formikProps} readOnly={readOnly} />
                  )}
                  {activeTab === PACKAGE_EDITOR_TAB_ENUM.Features && (
                    <PackageFeaturesTab
                      formikProps={formikProps}
                      availableFeatures={detail.available_features}
                      readOnly={readOnly}
                    />
                  )}
                  {activeTab === PACKAGE_EDITOR_TAB_ENUM.Claims && (
                    <PackageClaimsTab
                      formikProps={formikProps}
                      availableClaims={detail.available_claims}
                      readOnly={readOnly}
                    />
                  )}
                </div>
                {!readOnly && (
                  <div className="pt-3 space-y-2">
                    {contentError && (
                      <div className="rounded-md p-2 bg-danger-muted text-danger text-sm">{contentError}</div>
                    )}
                    {Object.keys(formikProps.errors).length > 0 && formikProps.submitCount > 0 && (
                      <div className="rounded-md p-2 bg-danger-muted text-danger text-sm">
                        {t("package_form_has_errors")}
                      </div>
                    )}
                    <ICButton
                      loading={submitLoading}
                      disabled={isBlocked || submitLoading}
                      className={cn("w-full", { "opacity-50 cursor-not-allowed": isBlocked })}
                    >
                      {t("submit")}
                    </ICButton>
                  </div>
                )}
              </Form>
            </FormRightModalWrapper>
          );
        }}
      </Formik>
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(value) => {
            setShowDiscardWarning(false);
            if (value) close(false);
          }}
        />
      )}
    </>
  );
};

export default PackageEditor;
